/* ------------------------------------------------------------------
   FAQs page — React match (app/faqs/page.tsx).

   Two interactions:
     1) Accordion: click question -> toggles panel, single-open per group.
        Uses JS-measured max-height (read scrollHeight, force reflow,
        then animate) for smooth open AND close — Grid 0fr↔1fr stutters
        on close in some browsers (see [[feedback-faq-stutter]]).
     2) Chip / aside highlight: IntersectionObserver picks the group
        whose top sits roughly in the middle of the viewport and marks
        the matching chip + side-nav link active.
   ------------------------------------------------------------------ */

(function () {
  const root = document.querySelector('[data-faqs-2026]');
  if (!root) return;

  // ---- Accordion ----------------------------------------------------
  // One openIndex per group (single-open-per-group, mirroring React).
  root.querySelectorAll('[data-faqs-group]').forEach(function (group) {
    const buttons = group.querySelectorAll('[data-faqs-btn]');

    buttons.forEach(function (btn, i) {
      const panel = group.querySelectorAll('[data-faqs-panel]')[i];
      if (!panel) return;

      btn.addEventListener('click', function () {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        // Close every sibling first.
        buttons.forEach(function (other, j) {
          if (other === btn) return;
          other.setAttribute('aria-expanded', 'false');
          const otherPanel = group.querySelectorAll('[data-faqs-panel]')[j];
          if (otherPanel) {
            otherPanel.dataset.open = 'false';
            otherPanel.style.maxHeight = '0px';
          }
        });

        if (isOpen) {
          btn.setAttribute('aria-expanded', 'false');
          panel.dataset.open = 'false';
          panel.style.maxHeight = '0px';
        } else {
          btn.setAttribute('aria-expanded', 'true');
          panel.dataset.open = 'true';
          // Measure target height, then animate from current (0) to it.
          // Force reflow so the browser registers the starting state.
          panel.style.maxHeight = '0px';
          // eslint-disable-next-line no-unused-expressions
          panel.offsetHeight;
          panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });

      // Arrow / Home / End keyboard nav between sibling buttons.
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          buttons[(i + 1) % buttons.length].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          buttons[(i - 1 + buttons.length) % buttons.length].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          buttons[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          buttons[buttons.length - 1].focus();
        }
      });
    });
  });

  // Recompute open panel heights on resize so wrapped content stays
  // visible at the new width.
  let resizeTick = null;
  window.addEventListener('resize', function () {
    if (resizeTick) cancelAnimationFrame(resizeTick);
    resizeTick = requestAnimationFrame(function () {
      root.querySelectorAll('[data-faqs-panel][data-open="true"]').forEach(function (p) {
        p.style.maxHeight = p.scrollHeight + 'px';
      });
    });
  });

  // ---- Chip + side-nav active highlight -----------------------------
  const groups = root.querySelectorAll('[data-faqs-group]');
  const chips = root.querySelectorAll('[data-faqs-chip]');
  const sideLinks = root.querySelectorAll('[data-faqs-aside-link]');

  // ---- Smooth-scroll chip + aside clicks ----------------------------
  // Lenis is initialised in theme.liquid with `anchors: true`, but it
  // doesn't always intercept clicks inside dynamic sections. We handle
  // it explicitly so chip → section navigation feels consistent.
  function smoothScrollTo(target) {
    if (!target) return;
    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      // Lenis handles its own easing + duration; offset matches the
      // section's `scroll-margin-top: 128px` so the heading isn't
      // hidden by the sticky header.
      window.lenis.scrollTo(target, { offset: -112, duration: 1.1 });
    } else if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function attachSmoothScroll(el) {
    el.addEventListener('click', function (e) {
      // Allow modifier-clicks (cmd/ctrl/middle) to fall through so power
      // users can still open in a new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      const href = el.getAttribute('href') || '';
      const id = href.charAt(0) === '#' ? href.slice(1) : null;
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      smoothScrollTo(target);
      // Update the URL hash without triggering a jump.
      if (history.pushState) history.pushState(null, '', '#' + id);
    });
  }

  chips.forEach(attachSmoothScroll);
  sideLinks.forEach(attachSmoothScroll);

  if (typeof IntersectionObserver === 'undefined') return;

  function setActive(id) {
    chips.forEach(function (c) {
      c.classList.toggle('is-active', c.dataset.faqsChip === id);
    });
    sideLinks.forEach(function (l) {
      l.classList.toggle('is-active', l.dataset.faqsAsideLink === id);
    });
  }

  const observer = new IntersectionObserver(
    function (entries) {
      const visible = entries
        .filter(function (e) { return e.isIntersecting; })
        .sort(function (a, b) {
          return a.boundingClientRect.top - b.boundingClientRect.top;
        })[0];
      if (visible && visible.target && visible.target.id) {
        setActive(visible.target.id);
      }
    },
    { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5] },
  );
  groups.forEach(function (g) { observer.observe(g); });

  // Seed the first group active so the hero isn't blank on load.
  if (groups[0] && groups[0].id) setActive(groups[0].id);
})();
